import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, BookOpen, Mail, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Buyer-facing /dashboard/support page (iter64 update).
 *
 *  - "Live chat" card now opens the floating SupportChatWidget via a custom event
 *    (the widget listens for `connexis:open-support` on the window object).
 *  - "Open a ticket" form posts a real first message into the support thread
 *    (subject + message are concatenated). The reply lands in the same thread the
 *    floating widget shows, so users have one continuous conversation.
 */
export default function Support() {
  const [busy, setBusy] = React.useState(false);

  const openChat = () => window.dispatchEvent(new CustomEvent("connexis:open-support"));

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const subject = (fd.get("subject") || "").toString().trim();
    const message = (fd.get("message") || "").toString().trim();
    if (!subject || !message) {
      toast.error("Subject and message are required");
      return;
    }
    setBusy(true);
    try {
      const body = `[${subject}]\n${message}`;
      await api.post("/support/me/messages", { body });
      toast.success("Sent — we'll reply within an hour during UK business hours");
      e.target.reset();
      // Open the chat panel so the user sees their message landed and can continue
      setTimeout(openChat, 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed — try the live chat widget bottom-right");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <SectionTitle title="Support" subtitle="Talk to your dedicated success team — live chat replies usually within an hour." />

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-5">
            <span className="inline-grid place-items-center size-10 rounded-md bg-slate-900 text-white"><Mail className="size-5" strokeWidth={1.6} /></span>
            <div className="mt-4 font-medium text-slate-900">Email</div>
            <a href="mailto:ops@connex.is" className="text-xs text-slate-500 mt-0.5 hover:text-[#0055FF]">ops@connex.is</a>
          </CardContent>
        </Card>

        <button
          onClick={openChat}
          className="text-left rounded-xl shadow-soft border border-slate-200 bg-white hover:border-[#0055FF]/40 transition-colors group"
          data-testid="support-livechat-card"
        >
          <div className="p-5">
            <span className="inline-grid place-items-center size-10 rounded-md bg-[#0055FF] text-white group-hover:scale-105 transition-transform"><MessageSquare className="size-5" strokeWidth={1.6} /></span>
            <div className="mt-4 font-medium text-slate-900">Live chat</div>
            <div className="text-xs text-slate-500 mt-0.5">Click to open · 9am – 6pm GMT, weekdays</div>
            <div className="text-[11px] text-[#0055FF] mt-2 font-medium">Start a conversation →</div>
          </div>
        </button>

        <Link to="/docs">
          <Card className="rounded-xl shadow-soft border-slate-200 hover:border-primary/40 transition-colors cursor-pointer" data-testid="support-docs-link">
            <CardContent className="p-5">
              <span className="inline-grid place-items-center size-10 rounded-md bg-slate-900 text-white"><BookOpen className="size-5" strokeWidth={1.6} /></span>
              <div className="mt-4 font-medium text-slate-900">Docs</div>
              <div className="text-xs text-slate-500 mt-0.5">connex.is/docs</div>
            </CardContent>
          </Card>
        </Link>
      </div>

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <span className="inline-grid place-items-center size-10 rounded-md bg-primary/10 text-primary"><LifeBuoy className="size-5" /></span>
            <div>
              <h3 className="font-medium text-slate-900">Send us a detailed brief</h3>
              <p className="text-[12px] text-slate-500">Prefer to write it all out? Send a structured message and we'll reply in the live chat thread.</p>
            </div>
          </div>
          <form onSubmit={submit} className="mt-5 space-y-4 max-w-2xl">
            <Input name="subject" required placeholder="Subject — e.g. Webhook 500s on mortgage campaign" data-testid="support-subject" />
            <Textarea name="message" required rows={5} placeholder="Include campaign IDs, buyer IDs or lead refs where relevant." data-testid="support-message" />
            <div className="flex items-center gap-3">
              <Button disabled={busy} type="submit" className="rounded-full" data-testid="support-submit">{busy ? "Sending…" : "Send to Connexis Support"}</Button>
              <button type="button" onClick={openChat} className="text-[12.5px] text-[#0055FF] hover:underline">or chat live →</button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
